/**
 * WebAuthn Client Library
 *
 * Utilities for registering and authenticating with passkeys.
 * Handles navigator.credentials API calls and byte conversion.
 */

interface RegistrationStartResponse {
  challenge: string;
  challengeId: string;
  rpId: string;
  rpName: string;
  user: {
    id: string;
    name: string;
    displayName: string;
  };
  pubKeyCredParams: PublicKeyCredentialParameters[];
  userVerification?: UserVerificationRequirement;
  excludeCredentials?: { id: string; type: "public-key" }[];
}

interface AuthenticationStartResponse {
  challenge: string;
  challengeId: string;
  rpId: string;
  timeout?: number;
  userVerification?: UserVerificationRequirement;
  allowCredentials?: { id: string; type: "public-key" }[];
}

interface AuthenticationResult {
  redirect_uri?: string;
  [key: string]: unknown;
}

/**
 * Check if WebAuthn is supported in this browser
 */
export function isWebAuthnSupported(): boolean {
  return !!(
    window.PublicKeyCredential &&
    navigator.credentials &&
    "create" in navigator.credentials &&
    "get" in navigator.credentials
  );
}

/**
 * Check if conditional mediation (form autofill) is supported
 */
export async function isConditionalMediationSupported(): Promise<boolean> {
  if (!isWebAuthnSupported()) {
    return false;
  }
  if (!("isConditionalMediationAvailable" in PublicKeyCredential)) {
    return false;
  }
  return await (
    PublicKeyCredential as typeof PublicKeyCredential & {
      isConditionalMediationAvailable(): Promise<boolean>;
    }
  ).isConditionalMediationAvailable();
}

/**
 * Convert base64url string to ArrayBuffer
 */
function base64urlToBuffer(base64url: string): ArrayBuffer {
  const base64 = base64url.replace(/-/g, "+").replace(/_/g, "/");
  const paddedBase64 = base64 + "=".repeat((4 - (base64.length % 4)) % 4);
  const binary = atob(paddedBase64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes.buffer;
}

/**
 * Convert ArrayBuffer to base64url string
 */
function bufferToBase64url(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  let binary = "";
  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=/g, "");
}

/**
 * Get RP ID from hostname
 */
function getRPId(): string {
  const hostname = window.location.hostname;
  const parts = hostname.split(".");
  if (parts.length <= 2) {
    return hostname;
  }
  return parts.slice(-2).join(".");
}

/**
 * Start passkey registration
 */
async function startRegistration(
  baseUrl: string
): Promise<RegistrationStartResponse> {
  const response = await fetch(`${baseUrl}/api/auth/passkey/register/begin`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
  });

  if (!response.ok) {
    throw new Error("Failed to start registration");
  }

  return await response.json();
}

/**
 * Complete passkey registration
 */
async function completeRegistration(
  baseUrl: string,
  challengeId: string,
  attestation: PublicKeyCredential
): Promise<unknown> {
  const response = attestation.response as AuthenticatorAttestationResponse;
  const transports = response.getTransports ? response.getTransports() : [];

  const fetchResponse = await fetch(
    `${baseUrl}/api/auth/passkey/register/complete`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({
        challenge_id: challengeId,
        credential_id: attestation.id,
        attestation_object: bufferToBase64url(response.attestationObject),
        client_data_json: bufferToBase64url(response.clientDataJSON),
        transports: transports,
      }),
    }
  );

  if (!fetchResponse.ok) {
    const error = (await fetchResponse.json()) as { error?: string };
    throw new Error(error.error || "Registration failed");
  }

  return await fetchResponse.json();
}

/**
 * Register a new passkey (full flow)
 */
export async function registerPasskey(baseUrl: string): Promise<unknown> {
  if (!isWebAuthnSupported()) {
    throw new Error("Your browser does not support passkeys");
  }

  try {
    const registrationStart = await startRegistration(baseUrl);
    const challenge = base64urlToBuffer(registrationStart.challenge);
    const userId = base64urlToBuffer(registrationStart.user.id);

    // Build excludeCredentials to prevent duplicate registrations
    const excludeCredentials = registrationStart.excludeCredentials?.map(
      (cred) => ({
        id: base64urlToBuffer(cred.id),
        type: cred.type as PublicKeyCredentialType,
      })
    );

    const credential = await navigator.credentials.create({
      publicKey: {
        challenge,
        rp: {
          id: registrationStart.rpId,
          name: registrationStart.rpName,
        },
        user: {
          id: userId,
          name: registrationStart.user.name,
          displayName: registrationStart.user.displayName,
        },
        pubKeyCredParams: registrationStart.pubKeyCredParams,
        attestation: "none",
        timeout: 60000,
        authenticatorSelection: {
          userVerification:
            registrationStart.userVerification || "preferred",
          residentKey: "required",
          requireResidentKey: true,
        },
        excludeCredentials,
      },
    });

    if (!credential) {
      throw new Error("User cancelled registration");
    }

    return await completeRegistration(
      baseUrl,
      registrationStart.challengeId,
      credential as PublicKeyCredential
    );
  } catch (err) {
    const error = err as Error;
    if (error.name === "NotAllowedError") {
      throw new Error("Registration was cancelled or timed out");
    }
    if (error.name === "InvalidStateError") {
      throw new Error("This authenticator is already registered");
    }
    if (error.name === "SecurityError") {
      throw new Error(
        "WebAuthn is not available in this context (requires HTTPS)"
      );
    }
    throw error;
  }
}

/**
 * Start passkey authentication
 */
async function startAuthentication(
  baseUrl: string
): Promise<AuthenticationStartResponse> {
  const response = await fetch(
    `${baseUrl}/api/auth/passkey/authenticate/begin`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
    }
  );

  if (!response.ok) {
    if (response.status === 429) {
      throw new Error("Too many attempts, please wait");
    }
    throw new Error("Failed to start authentication");
  }

  return await response.json();
}

/**
 * Complete passkey authentication
 */
async function completeAuthentication(
  baseUrl: string,
  challengeId: string,
  assertion: PublicKeyCredential,
  redirectUri?: string
): Promise<AuthenticationResult> {
  const response = assertion.response as AuthenticatorAssertionResponse;

  const requestBody: Record<string, string> = {
    challenge_id: challengeId,
    credential_id: assertion.id,
    authenticator_data: bufferToBase64url(response.authenticatorData),
    client_data_json: bufferToBase64url(response.clientDataJSON),
    signature: bufferToBase64url(response.signature),
    user_handle: response.userHandle
      ? bufferToBase64url(response.userHandle)
      : "",
  };

  if (redirectUri) {
    requestBody.redirect_uri = redirectUri;
  }

  const fetchResponse = await fetch(
    `${baseUrl}/api/auth/passkey/authenticate/complete`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify(requestBody),
    }
  );

  if (!fetchResponse.ok) {
    const error = (await fetchResponse.json()) as { error?: string };
    throw new Error(error.error || "Authentication failed");
  }

  return await fetchResponse.json();
}

/**
 * Authenticate with passkey (full flow)
 */
export async function authenticateWithPasskey(
  baseUrl: string,
  redirectUri?: string,
  abortController?: AbortController
): Promise<AuthenticationResult> {
  if (!isWebAuthnSupported()) {
    throw new Error("Your browser does not support passkeys");
  }

  try {
    const authStart = await startAuthentication(baseUrl);
    const challenge = base64urlToBuffer(authStart.challenge);

    // Use server-provided rpId for consistency with registration
    // Fall back to client-derived if server doesn't provide one
    const rpId = authStart.rpId || getRPId();

    // Note: Do NOT include allowCredentials - omitting it (undefined) triggers
    // different browser behavior than allowCredentials: []. Safari may handle
    // non-discoverable credentials differently when allowCredentials is undefined.
    const publicKeyOptions: PublicKeyCredentialRequestOptions = {
      challenge,
      rpId,
      userVerification: "preferred",
      timeout: 60000,
    };

    const options: CredentialRequestOptions = {
      publicKey: publicKeyOptions,
    };

    if (abortController) {
      options.signal = abortController.signal;
    }

    const assertion = await navigator.credentials.get(options);

    if (!assertion) {
      throw new Error("User cancelled authentication");
    }

    return await completeAuthentication(
      baseUrl,
      authStart.challengeId,
      assertion as PublicKeyCredential,
      redirectUri
    );
  } catch (error) {
    if (error instanceof Error) {
      if (error.name === "AbortError") {
        throw new Error("Authentication was cancelled");
      }
      if (error.name === "NotAllowedError") {
        throw new Error("Authentication was cancelled or timed out");
      }
      if (error.name === "SecurityError") {
        throw new Error(
          "WebAuthn is not available in this context (requires HTTPS)"
        );
      }
    }
    throw error;
  }
}

/**
 * Start conditional passkey authentication (form autofill)
 */
export async function startConditionalAuthentication(
  baseUrl: string,
  redirectUri: string | undefined,
  abortController: AbortController
): Promise<AuthenticationResult> {
  const supported = await isConditionalMediationSupported();
  if (!supported) {
    throw new Error("Conditional mediation not supported");
  }

  try {
    const authStart = await startAuthentication(baseUrl);
    const challenge = base64urlToBuffer(authStart.challenge);

    // Use server-provided rpId for consistency with registration
    const rpId = authStart.rpId || getRPId();

    const assertion = await navigator.credentials.get({
      publicKey: {
        challenge,
        rpId,
        userVerification: "preferred",
        timeout: 300000,
        // Note: For conditional mediation, empty allowCredentials is required
        allowCredentials: [],
      },
      mediation: "conditional",
      signal: abortController.signal,
    });

    if (!assertion) {
      throw new Error("User cancelled authentication");
    }

    return await completeAuthentication(
      baseUrl,
      authStart.challengeId,
      assertion as PublicKeyCredential,
      redirectUri
    );
  } catch (error) {
    if (error instanceof Error) {
      if (error.name === "AbortError") {
        throw new Error("Conditional authentication was cancelled");
      }
      if (error.name === "NotAllowedError") {
        throw new Error("Authentication was cancelled or timed out");
      }
    }
    throw error;
  }
}
